/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { QUERY_KEYS } from 'constants/queryKeys';

import {
  availabilityServices,
  fleetVehiclesServices,
  servicePointsServices,
} from '@services';
import { showError } from '@utils';
import { selectLoggedInUserState } from '@store';
import { ENTITY_TYPES_COMMENTS } from '@constants';
import { useInfinitePagination, useAssignees } from '@hooks';

import {
  getVehicleComments,
  addVehicleComment,
  deleteVehicleComment,
} from 'services/vehicles/vehiclesServices';
import {
  handleColors,
  handleDealers,
  handleVehicle,
  handleInsurances,
  handleFinancingProviders,
  handleRoadsideAssistances,
  handleServicePointsAll,
  handleVehicleServicePoints,
  handleInfiniteQueryData,
  handleFleetVehiclesComments,
} from './utils';
import {
  SERVICE_POINTS_QUERY_PAYLOAD,
  VEHICLE_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
} from './constants';
import { useFleetVehicleErrors } from './useFleetVehicleErrors';

export const useFleetVehicleOperations = () => {
  const {
    query: { vehicleId, ...restQueryParams },
  } = useRouter();

  const historyTab = restQueryParams?.historyTab;

  const isHistoryTabComment = historyTab === 'comment';

  const loggedInUser = useSelector(selectLoggedInUserState);

  const { getAssigneeName } = useAssignees();

  const availabilityResponse = useQuery(
    [QUERY_KEYS.GET_AVAILABILITY_ENUMS_KEY],
    () => availabilityServices.getAvailabilityEnums()
  );

  const changelogResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_CHANGELOG_KEY, vehicleId],
    () => fleetVehiclesServices.getVehicleChangelog(vehicleId),
    {
      enabled: !!vehicleId,
    }
  );

  const servicePointsAllResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_ALL_SERVICE_POINTS_KEY],
    () => servicePointsServices.getServicePoints(SERVICE_POINTS_QUERY_PAYLOAD)
  );

  const insurancesResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_INSURANCES_KEY],
    () => fleetVehiclesServices.getInsurances(SERVICE_POINTS_QUERY_PAYLOAD)
  );

  const roadsideAssistancesResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_ROAD_SIDE_ASSISTANCE_KEY],
    () =>
      fleetVehiclesServices.getRoadsideAssistances(SERVICE_POINTS_QUERY_PAYLOAD)
  );

  const colorsResponse = useQuery([QUERY_KEYS.GET_VEHICLE_COLORS_KEY], () =>
    fleetVehiclesServices.getVehicleColors()
  );

  const dealersResponse = useQuery([QUERY_KEYS.GET_VEHICLE_DEALERS_KEY], () =>
    fleetVehiclesServices.getVehicleDealers()
  );

  const providersResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_PROVIDERS_KEY],
    () => fleetVehiclesServices.getVehicleProviders()
  );

  const sourcingTypesResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_SOURCING_TYPES_KEY],
    () => fleetVehiclesServices.getVehicleSourcingTypes()
  );

  const vehicleResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_BY_ID_KEY, vehicleId],
    () => fleetVehiclesServices?.getVehicleById(vehicleId),
    {
      enabled:
        availabilityResponse.isFetched &&
        !!vehicleId &&
        servicePointsAllResponse.isFetched &&
        insurancesResponse.isFetched &&
        roadsideAssistancesResponse.isFetched &&
        colorsResponse.isFetched &&
        dealersResponse.isFetched &&
        providersResponse.isFetched,
    }
  );

  const vehiclesServicePointsResponse = useQuery(
    [
      QUERY_KEYS.GET_VEHICLE_SERVICE_POINTS_KEY,
      vehicleResponse?.data?.data?.id,
    ],
    () =>
      fleetVehiclesServices.getVehicleServicePointsByID({
        id: vehicleResponse?.data?.data?.id,
        status: 1,
      }),
    {
      enabled: !!vehicleResponse?.data?.data?.id,
    }
  );

  const fleetVehiclesCommentsResponse = useQuery(
    [QUERY_KEYS.GET_FLEET_VEHICLES_COMMENTS_KEY, vehicleId],
    () => getVehicleComments(vehicleId),
    {
      enabled: !!vehicleId && isHistoryTabComment,
    }
  );

  const fleetVehiclesComments = useMemo(() => {
    return handleFleetVehiclesComments(
      fleetVehiclesCommentsResponse?.data?.data || [],
      loggedInUser,
      getAssigneeName
    );
  }, [fleetVehiclesCommentsResponse?.data]);

  const vehicleHistoryInfiniteQuery = useInfinitePagination({
    queryKey: QUERY_KEYS.GET_VEHICLE_HISTORY_CHANGES_KEY,
    params: {
      vehicleId,
      pageSize: VEHICLE_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
    },
    enabled: Boolean(vehicleId),
    initialPage: 1,
    queryFn: fleetVehiclesServices.getVehicleHistoryChanges,
  });

  const vehicleHistoryQueryItemsHandled = useMemo(() => {
    return handleInfiniteQueryData(
      vehicleHistoryInfiniteQuery?.items,
      servicePointsAllResponse?.data?.data || [],
      insurancesResponse?.data?.data || [],
      roadsideAssistancesResponse?.data?.data || [],
      loggedInUser,
      getAssigneeName
    );
  }, [
    vehicleHistoryInfiniteQuery,
    servicePointsAllResponse?.data,
    insurancesResponse?.data,
    roadsideAssistancesResponse?.data,
  ]);

  const fleetVehiclesDeleteCommentMutation = useMutation(deleteVehicleComment);

  const vehicleHistoryQueryData = useMemo(() => {
    if (isHistoryTabComment) {
      return {
        ...fleetVehiclesCommentsResponse,
        items: fleetVehiclesComments,
      };
    }

    return {
      ...vehicleHistoryInfiniteQuery,
      items: vehicleHistoryQueryItemsHandled,
    };
  }, [
    isHistoryTabComment,
    fleetVehiclesCommentsResponse,
    fleetVehiclesComments,
    vehicleHistoryInfiniteQuery,
    vehicleHistoryQueryItemsHandled,
  ]);

  const addCommentMutation = useMutation(addVehicleComment);

  const handleFleetVehiclesAddComment = (commentData) => {
    addCommentMutation.mutate(
      {
        entityId: vehicleId,
        entityType: ENTITY_TYPES_COMMENTS.vehicles,
        comment: commentData.content,
      },
      {
        onSuccess: () => {
          fleetVehiclesCommentsResponse?.refetch?.();
          vehicleHistoryInfiniteQuery?.refetch?.();
        },
        onError: (error) => {
          showError(error);
        },
      }
    );
  };

  const handleFleetVehiclesDeleteComment = useCallback(
    (commentItem) => {
      const commentId = commentItem?.id || commentItem?.resourceId;
      if (!commentId) return;

      fleetVehiclesDeleteCommentMutation.mutate(commentId, {
        onSuccess: () => {
          fleetVehiclesCommentsResponse?.refetch?.();
          vehicleHistoryInfiniteQuery?.refetch?.();
        },
        onError: (error) => {
          showError(error);
        },
      });
    },
    [fleetVehiclesDeleteCommentMutation, vehicleHistoryQueryData, showError]
  );

  const vehicleHistoryQuery = useMemo(() => {
    return {
      ...vehicleHistoryQueryData,
      onAddComment: handleFleetVehiclesAddComment,
      onDeleteComment: handleFleetVehiclesDeleteComment,
    };
  }, [vehicleHistoryQueryData, handleFleetVehiclesDeleteComment]);

  const colors = useMemo(
    () => handleColors(colorsResponse?.data?.data || []),
    [colorsResponse?.data]
  );

  const dealers = useMemo(
    () => handleDealers(dealersResponse?.data?.data || []),
    [dealersResponse?.data]
  );

  const financingProviders = useMemo(
    () => handleFinancingProviders(providersResponse?.data?.data || []),
    [providersResponse?.data]
  );
  const servicePoints = useMemo(
    () =>
      handleVehicleServicePoints(
        vehiclesServicePointsResponse?.data?.data || []
      ),
    [vehiclesServicePointsResponse?.data]
  );

  const servicePointsAll = useMemo(
    () => handleServicePointsAll(servicePointsAllResponse?.data?.data || []),
    [servicePointsAllResponse?.data?.data]
  );

  const insurances = useMemo(
    () => handleInsurances(insurancesResponse?.data?.data || []),
    [insurancesResponse?.data?.data]
  );

  const roadsideAssistances = useMemo(
    () =>
      handleRoadsideAssistances(roadsideAssistancesResponse?.data?.data || []),
    [roadsideAssistancesResponse?.data?.data]
  );
  const sourcingTypes = sourcingTypesResponse?.data?.data || [];
  const vehicle = handleVehicle({
    vehicle: vehicleResponse?.data?.data || {},
    availabilityEnums: availabilityResponse?.data || {},
    roadsideAssistances,
    insurances,
    colors,
    dealers,
    financingProviders,
    servicePointsAll,
  });

  const loading =
    changelogResponse.isLoading ||
    servicePointsAllResponse.isLoading ||
    insurancesResponse.isLoading ||
    roadsideAssistancesResponse.isLoading ||
    colorsResponse.isLoading ||
    dealersResponse.isLoading ||
    providersResponse.isLoading ||
    availabilityResponse.isLoading ||
    vehiclesServicePointsResponse.isLoading ||
    vehicleResponse.isLoading ||
    sourcingTypesResponse.isLoading;

  useFleetVehicleErrors({
    changelogResponse,
    servicePointsAllResponse,
    insurancesResponse,
    roadsideAssistancesResponse,
    colorsResponse,
    dealersResponse,
    providersResponse,
    availabilityResponse,
    vehiclesServicePointsResponse,
    vehicleResponse,
    sourcingTypesResponse,
  });

  return {
    servicePointsAll,
    insurances,
    roadsideAssistances,
    financingProviders,
    loading,
    vehicleId,
    servicePoints,
    vehicle,
    colors,
    dealers,
    sourcingTypes,
    vehicleHistoryQuery,
    getVehicle: vehicleResponse.refetch,
    getVehicleServicePoints: vehiclesServicePointsResponse.refetch,
    getFleetVehicleChangelog: changelogResponse.refetch,
  };
};
